import { makeStyles, tokens, Text, Spinner } from '@fluentui/react-components';
import {
  Add24Regular,
  ArrowSync24Regular,
  Note24Regular,
  Person24Regular,
  Money24Regular,
  Edit24Regular,
  Receipt24Regular,
} from '@fluentui/react-icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useReferralActivities } from '@/api/hooks/useReferrals';
import type { ReferralActivityType } from '@/types/referral';

dayjs.extend(relativeTime);

const useStyles = makeStyles({
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    paddingLeft: tokens.spacingHorizontalM,
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  item: {
    position: 'relative',
    paddingLeft: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalM,
  },
  icon: {
    position: 'absolute',
    left: '-23px',
    top: '-2px',
    fontSize: '20px',
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: '2px',
    paddingBottom: '2px',
  },
  description: {
    display: 'block',
    lineHeight: '20px',
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: tokens.spacingVerticalM,
  },
});

const ACTIVITY_ICONS: Record<ReferralActivityType, typeof Add24Regular> = {
  Created: Add24Regular,
  StatusChanged: ArrowSync24Regular,
  AdminNoteUpdated: Note24Regular,
  Assigned: Person24Regular,
  RevenueUpdated: Money24Regular,
  ReferrerNoteUpdated: Edit24Regular,
  CommissionCreated: Receipt24Regular,
  CommissionStatusChanged: Receipt24Regular,
};

const ACTIVITY_COLORS: Record<ReferralActivityType, string> = {
  Created: tokens.colorPaletteGreenForeground1,
  StatusChanged: tokens.colorBrandForeground1,
  AdminNoteUpdated: tokens.colorNeutralForeground2,
  Assigned: tokens.colorPaletteBerryForeground1,
  RevenueUpdated: tokens.colorPaletteGreenForeground1,
  ReferrerNoteUpdated: tokens.colorNeutralForeground2,
  CommissionCreated: tokens.colorPaletteGreenForeground1,
  CommissionStatusChanged: tokens.colorPaletteDarkOrangeForeground1,
};

interface ActivityTimelineProps {
  referralId: number;
}

export function ActivityTimeline({ referralId }: ActivityTimelineProps) {
  const styles = useStyles();
  const { data: activities, isLoading } = useReferralActivities(referralId);

  if (isLoading) {
    return <Spinner size="tiny" label="Loading activity..." />;
  }

  if (!activities || activities.length === 0) {
    return <Text className={styles.empty}>No activity yet.</Text>;
  }

  return (
    <div className={styles.timeline}>
      {activities.map((activity) => {
        const Icon = ACTIVITY_ICONS[activity.activityType] ?? ArrowSync24Regular;
        const color = ACTIVITY_COLORS[activity.activityType] ?? tokens.colorNeutralForeground2;

        return (
          <div key={activity.id} className={styles.item}>
            <Icon className={styles.icon} style={{ color }} />
            <Text size={300} className={styles.description}>
              {activity.description}
            </Text>
            <Text className={styles.meta}>
              by {activity.performedByName} &middot; {dayjs(activity.performedAt).fromNow()}
            </Text>
          </div>
        );
      })}
    </div>
  );
}
